import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
  DashboardMetadata,
  GetChartsQuery,
  GetChartsQueryVariables,
  GetDashboardQuery,
  GetDashboardQueryVariables,
  GetQueryResultQuery,
  GetQueryResultQueryVariables,
  QueryResultInput,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppService } from '../../../shared/services/app.service';
import { ChartDataWithId, ChartMetadataExtended, DataArrays } from '../../models/charts';
import { ChartDataService } from '../../services/chart-data.service';
import { Subject } from 'rxjs';
import { GetCharts, GetDashboard, GetQueryResult } from '../../../../graphql/queries';
import { DashboardPreviewComponent } from '../dashboard-preview/dashboard-preview.component';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';

type QueryResultRow = Record<string, unknown>;

@Component({
  selector: 'app-create-dashboard',
  templateUrl: './create-dashboard.component.html',
  styleUrl: './create-dashboard.component.scss',
})
export class CreateDashboardComponent implements OnInit, OnDestroy {
  private readonly queryResultPageSize = 2000;

  @ViewChild('track', { static: false }) track!: ElementRef<HTMLDivElement>;
  @ViewChild(DashboardPreviewComponent) dashboardPreviewComponent?: DashboardPreviewComponent;

  private destroy$ = new Subject<void>();

  dashboardDetails: FormGroup;
  isLoading = false;

  orgId = '';
  charts: ChartMetadataExtended[] = [];
  chartsToDisplay: ChartMetadataExtended[] = [];
  selectedCharts: ChartMetadataExtended[] = [];
  allChartsResultData: ChartDataWithId = {};

  activeDashboardData?: DashboardMetadata;
  isEditMode = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private appService: AppService,
    private chartDataService: ChartDataService,
    @Inject(NZ_MODAL_DATA) public data: { dashboardId?: string } | null,
  ) {
    this.dashboardDetails = this.fb.group({
      name: this.fb.control('', [Validators.required, Validators.maxLength(30)]),
      description: this.fb.control('', [Validators.required, Validators.maxLength(120)]),
      searchValue: this.fb.control(''),
    });
  }

  ngOnInit(): void {
    this.orgId = this.userManagementService.getOrgId();
    if (!this.orgId) {
      this.nzMessageService.error('Organization not found. Please try again.');
      return;
    }

    this.getAllCharts();

    const dashboardId = this.data?.dashboardId;
    if (dashboardId) {
      this.isEditMode = true;
      this.getDashboardData(dashboardId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.chartDataService.allChartsResultData.next({});
    this.chartDataService.selectedChartsForDashboard.next([]);
  }

  private sortCharts(): void {
    this.chartsToDisplay = this.charts.sort((a, b) => {
      const dateA = new Date(a.lastModifiedAt || 0);
      const dateB = new Date(b.lastModifiedAt || 0);

      return dateB.getTime() - dateA.getTime();
    });
  }

  reset(): void {
    this.searchValueFormControl?.reset();
    this.chartsToDisplay = [...this.charts];
  }

  search(): void {
    const searchValueLower = (this.searchValueFormControl?.value || '').toLowerCase();
    this.chartsToDisplay = this.charts.filter((chart) => {
      const chartName = chart.name?.toLowerCase() || '';
      const chartType = chart.chartType?.toLowerCase() || '';
      const lastModifiedBy = chart.lastModifiedBy?.toLowerCase() || '';
      const createdBy = chart.createdBy?.toLowerCase() || '';

      let chartTitle = '';
      try {
        chartTitle = JSON.parse(chart.config || '{}')?.chartTitle?.toLowerCase() || '';
      } catch {
        chartTitle = '';
      }

      return (
        chartName.includes(searchValueLower) ||
        chartType.includes(searchValueLower) ||
        lastModifiedBy.includes(searchValueLower) ||
        createdBy.includes(searchValueLower) ||
        chartTitle.includes(searchValueLower)
      );
    });
  }

  scrollNext(): void {
    const track = this.track.nativeElement;
    const firstItem = track.querySelector('.carousel-item') as HTMLElement | null;
    if (firstItem) {
      track.scrollBy({ left: firstItem.offsetWidth, behavior: 'smooth' });
    }
  }

  scrollPrev(): void {
    const track = this.track.nativeElement;
    const firstItem = track.querySelector('.carousel-item') as HTMLElement | null;
    if (firstItem) {
      track.scrollBy({ left: -firstItem.offsetWidth, behavior: 'smooth' });
    }
  }

  getAllCharts(): void {
    this.isLoading = true;
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        orgId: this.orgId,
      })
      .then((response) => {
        const result: ChartMetadataExtended[] = (response?.data?.GetCharts || []).map((chartItem) => ({
          ...chartItem,
          chartDisplayImage: `${this.appService.domainUrl}/images/chart/${this.orgId}/${chartItem?.chartId}/chart.svg`,
        })) as ChartMetadataExtended[];

        this.chartsToDisplay = this.charts = result;
        this.sortCharts();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  selectChart(chart: ChartMetadataExtended): void {
    if (this.isChartSelected(chart.chartId)) {
      this.dashboardPreviewComponent?.removeWidget(chart.chartId);
      return;
    }

    this.getSelectedChartQueryResult(chart);
  }

  isChartSelected(chartId: string): boolean {
    const existingCharts = this.chartDataService.selectedChartsForDashboard.value;
    return existingCharts.some((item) => item.chartId === chartId);
  }

  async getSelectedChartQueryResult(chart: ChartMetadataExtended): Promise<void> {
    this.isLoading = true;

    const input: QueryResultInput = {
      orgId: chart.orgId,
      queryId: chart.queryId || '',
      executionId: chart.executionId || '',
      athenaExecutionId: chart.athenaExecutionId || '',
    };

    try {
      const rowsData = await this.getAllQueryResultRows(input);
      if (rowsData.length > 0) {
        const chartPreviewData = rowsData.reduce<DataArrays>((acc, obj) => {
          Object.entries(obj).forEach(([key, value]) => {
            if (!acc[key]) acc[key] = [];
            acc[key].push(value as string | number);
          });
          return acc;
        }, {});

        const existingData = this.chartDataService.allChartsResultData.value;
        this.chartDataService.allChartsResultData.next({
          ...existingData,
          [chart.chartId]: chartPreviewData,
        });

        const existingSelectedCharts = this.chartDataService.selectedChartsForDashboard.value;
        const isChartAlreadySelected = existingSelectedCharts.some((item) => item.chartId === chart.chartId);
        if (!isChartAlreadySelected) {
          this.chartDataService.selectedChartsForDashboard.next([...existingSelectedCharts, chart]);
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.nzMessageService.error(`Error getting data for rendering chart ${message}`);
    } finally {
      this.isLoading = false;
    }
  }

  private async getAllQueryResultRows(input: QueryResultInput): Promise<QueryResultRow[]> {
    const rows: QueryResultRow[] = [];
    let pageNumber = 1;

    while (true) {
      const response = await this.apiService.query<GetQueryResultQuery, GetQueryResultQueryVariables>(GetQueryResult, {
        input: {
          ...input,
          maxRows: this.queryResultPageSize,
          pageNumber,
        },
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((error) => error.message).join(', '));
      }

      const pageRows = response?.data?.GetQueryResult?.data?.rows;
      const parsedPageRows: unknown = pageRows ? JSON.parse(pageRows) : [];

      if (!Array.isArray(parsedPageRows) || parsedPageRows.length === 0) {
        break;
      }

      rows.push(...(parsedPageRows as QueryResultRow[]));
      if (parsedPageRows.length < this.queryResultPageSize) {
        break;
      }

      pageNumber += 1;
    }

    return rows;
  }

  getDashboardData(dashboardId: string): void {
    this.isLoading = true;
    this.apiService
      .query<GetDashboardQuery, GetDashboardQueryVariables>(GetDashboard, {
        dashboardId,
        orgId: this.orgId,
      })
      .then((response) => {
        this.activeDashboardData = response.data?.GetDashboard as DashboardMetadata;
        this.dashboardDetails.patchValue({
          name: this.activeDashboardData?.name,
          description: this.activeDashboardData?.description,
        });

        if (this.activeDashboardData?.chartIds?.length) {
          this.getChartsData(this.activeDashboardData.chartIds);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Unable to load dashboard for edit, ${error?.message || error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getChartsData(chartIds: string[]): void {
    this.isLoading = true;
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        orgId: this.orgId,
      })
      .then((response) => {
        const result = response.data?.GetCharts || [];
        const selectedCharts = result.filter((chart) => chart?.chartId && chartIds.includes(chart.chartId));
        selectedCharts.forEach((chart) => {
          const extendedChart: ChartMetadataExtended = {
            ...chart,
            chartDisplayImage: `${this.appService.domainUrl}/images/chart/${this.orgId}/${chart.chartId}/chart.svg`,
          };
          this.selectChart(extendedChart);
        });
      })
      .catch((error) => {
        this.nzMessageService.error(`Unable to load dashboard charts for edit, ${error?.message || error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get searchValueFormControl(): UntypedFormControl {
    return this.dashboardDetails.get('searchValue') as UntypedFormControl;
  }
}
